/**
 * Qué pantallas hay y en qué grupo va cada una.
 *
 * Vive aparte del componente por una razón que no es de prolijidad: **hay una prueba que compara
 * esta lista con las rutas del enrutador** y falla si alguien agrega una pantalla y se olvida de
 * darle lugar en el menú (NAV-02). Una pantalla sin entrada acá existe, responde y no la encuentra
 * nadie, que es la peor de las tres formas de estar rota porque no da ningún síntoma.
 *
 * Los grupos son **por lo que la gente hace**, no por cómo está organizado el código. «Comisiones»
 * está en Plata y no en Gente porque quien la mira está pensando en cuánto tiene que pagar, no en
 * quién trabaja acá.
 */

export interface Seccion {
  readonly ruta: string;
  readonly clave: string;
  readonly permiso?: string;
  /**
   * Sólo existe donde el sistema habla con el fisco electrónico —hoy, Argentina con ARCA—. El
   * Libro de IVA Digital de un comercio uruguayo sería un archivo que no se presenta en ningún
   * lado (E35).
   */
  readonly soloConFiscoElectronico?: boolean;
  /**
   * El nombre del ícono, no el ícono.
   *
   * Este archivo lo lee una prueba que lo compara con las rutas del enrutador, y esa prueba corre
   * sin navegador: importar componentes de React acá la obligaría a montar medio árbol para
   * comparar dos listas de textos. El nombre se resuelve a un dibujo en la navegación, que es
   * donde hay React.
   */
  readonly icono: NombreDeIcono;
}

/** Los íconos que la navegación sabe dibujar. La prueba de cobertura verifica que no falte ninguno. */
export type NombreDeIcono =
  | 'Banknote'
  | 'BookOpen'
  | 'BookUser'
  | 'Boxes'
  | 'Building2'
  | 'Calculator'
  | 'CalendarRange'
  | 'ClipboardList'
  | 'FileCog'
  | 'FileOutput'
  | 'FolderTree'
  | 'Hammer'
  | 'Hash'
  | 'History'
  | 'Home'
  | 'Landmark'
  | 'Library'
  | 'ListTree'
  | 'Package'
  | 'Percent'
  | 'ReceiptText'
  | 'ScanBarcode'
  | 'ServerCog'
  | 'Settings'
  | 'SlidersHorizontal'
  | 'ShoppingCart'
  | 'Tags'
  | 'Truck'
  | 'Undo2'
  | 'Upload'
  | 'UserCog'
  | 'Users'
  | 'Wallet'
  | 'Warehouse';

export interface Grupo {
  readonly clave: string;
  readonly secciones: readonly Seccion[];
}

/**
 * Inicio no está en ningún grupo, y es a propósito: es el lugar al que se vuelve, no una pantalla
 * más. Va suelto arriba de todo, como el logo de un sitio.
 */
export const INICIO: Seccion = { ruta: '/', clave: 'navegacion.inicio', icono: 'Home' };

/**
 * La administración de la instalación.
 *
 * Fuera de los grupos y sin permiso de empresa, porque no es de ninguna empresa: la ve **sólo
 * quien administra la plataforma**, y eso no es un permiso de rol sino una condición de la
 * persona, que el menú lee del perfil y el servidor verifica en la base en cada pedido.
 */
export const PLATAFORMA: Seccion = {
  ruta: '/plataforma',
  clave: 'navegacion.plataforma',
  icono: 'ServerCog',
};

export const GRUPOS: readonly Grupo[] = [
  {
    clave: 'navegacion.grupos.vender',
    secciones: [
      // Vender va primero adentro de su grupo y su grupo va primero: es lo que se hace todo el día.
      {
        ruta: '/mostrador',
        clave: 'navegacion.mostrador',
        icono: 'ScanBarcode',
        permiso: 'VENTAS.COMPROBANTE.EMITIR',
      },
      {
        ruta: '/mayorista',
        clave: 'navegacion.mayorista',
        icono: 'ClipboardList',
        permiso: 'VENTAS.COMPROBANTE.EMITIR',
      },
      {
        ruta: '/devoluciones',
        clave: 'navegacion.devoluciones',
        icono: 'Undo2',
        permiso: 'VENTAS.DEVOLUCION.REGISTRAR',
      },
      {
        ruta: '/conversiones',
        clave: 'navegacion.conversiones',
        icono: 'FileOutput',
        permiso: 'VENTAS.COMPROBANTE.EMITIR',
      },
      {
        ruta: '/clientes',
        clave: 'navegacion.clientes',
        icono: 'Users',
        permiso: 'TERCEROS.CLIENTE.VER',
      },
    ],
  },
  {
    clave: 'navegacion.grupos.stock',
    secciones: [
      {
        ruta: '/articulos',
        clave: 'navegacion.catalogo',
        icono: 'Package',
        permiso: 'CATALOGO.ARTICULO.VER',
      },
      {
        ruta: '/rubros',
        clave: 'navegacion.rubros',
        icono: 'FolderTree',
        permiso: 'CATALOGO.CATEGORIA.VER',
      },
      {
        ruta: '/precios',
        clave: 'navegacion.precios',
        icono: 'Tags',
        permiso: 'PRECIOS.PRECIO.VER',
      },
      {
        ruta: '/recalcular-precios',
        clave: 'navegacion.recalculo',
        icono: 'Calculator',
        permiso: 'PRECIOS.PRECIO.VER',
      },
      {
        ruta: '/existencias',
        clave: 'navegacion.existencias',
        icono: 'Boxes',
        permiso: 'EXISTENCIAS.SALDO.VER',
      },
      {
        ruta: '/produccion',
        clave: 'navegacion.produccion',
        icono: 'Hammer',
        permiso: 'PRODUCCION.RECETA.VER',
      },
    ],
  },
  {
    clave: 'navegacion.grupos.comprar',
    secciones: [
      {
        ruta: '/compras',
        clave: 'navegacion.compras',
        icono: 'ShoppingCart',
        permiso: 'COMPRAS.ORDEN.VER',
      },
      {
        ruta: '/proveedores',
        clave: 'navegacion.proveedores',
        icono: 'Truck',
        permiso: 'TERCEROS.PROVEEDOR.VER',
      },
      {
        ruta: '/pagos',
        clave: 'navegacion.pagos',
        icono: 'Banknote',
        permiso: 'CUENTAS.PAGO.REGISTRAR',
      },
    ],
  },
  {
    clave: 'navegacion.grupos.plata',
    secciones: [
      { ruta: '/caja', clave: 'navegacion.caja', icono: 'Wallet', permiso: 'CAJA.TURNO.VER' },
      {
        ruta: '/bancos',
        clave: 'navegacion.bancos',
        icono: 'Landmark',
        permiso: 'BANCOS.CUENTA.VER',
      },
      {
        ruta: '/cuentas',
        clave: 'navegacion.cuentas',
        icono: 'BookUser',
        permiso: 'CUENTAS.CUENTA.VER',
      },
      // Acá y no en Gente: quien la mira está pensando en cuánto tiene que pagar este mes.
      {
        ruta: '/comisiones',
        clave: 'navegacion.comisiones',
        icono: 'Percent',
        permiso: 'COMISIONES.DEVENGAMIENTO.VER',
      },
    ],
  },
  {
    clave: 'navegacion.grupos.contabilidad',
    secciones: [
      {
        ruta: '/asientos',
        clave: 'navegacion.asientos',
        icono: 'BookOpen',
        permiso: 'CONTABILIDAD.ASIENTO.VER',
      },
      {
        ruta: '/libros',
        clave: 'navegacion.libros',
        icono: 'Library',
        permiso: 'CONTABILIDAD.LIBRO.VER',
      },
      {
        ruta: '/libro-de-iva',
        clave: 'navegacion.libroDeIva',
        icono: 'ReceiptText',
        permiso: 'IMPUESTOS.LIBRO_DE_IVA.GENERAR',
        soloConFiscoElectronico: true,
      },
      {
        ruta: '/ejercicios',
        clave: 'navegacion.ejercicios',
        icono: 'CalendarRange',
        permiso: 'CONTABILIDAD.ASIENTO.VER',
      },
      {
        ruta: '/plan-de-cuentas',
        clave: 'navegacion.contabilidad',
        icono: 'ListTree',
        permiso: 'CONTABILIDAD.PLAN.VER',
      },
    ],
  },
  {
    clave: 'navegacion.grupos.configurar',
    secciones: [
      {
        ruta: '/sucursales',
        clave: 'navegacion.sucursales',
        icono: 'Building2',
        permiso: 'EMPRESA.SUCURSAL.VER',
      },
      {
        ruta: '/estructura',
        clave: 'navegacion.estructura',
        icono: 'Warehouse',
        permiso: 'EMPRESA.DEPOSITO.VER',
      },
      {
        ruta: '/numeradores',
        clave: 'navegacion.numeradores',
        icono: 'Hash',
        permiso: 'VENTAS.TIPO_DE_COMPROBANTE.VER',
      },
      {
        ruta: '/tipos-de-comprobante',
        clave: 'navegacion.tiposDeComprobante',
        icono: 'FileCog',
        permiso: 'VENTAS.TIPO_DE_COMPROBANTE.VER',
      },
      {
        ruta: '/campos',
        clave: 'navegacion.campos',
        icono: 'SlidersHorizontal',
        permiso: 'EMPRESA.CAMPO.VER',
      },
      {
        ruta: '/configuracion',
        clave: 'navegacion.configuracion',
        icono: 'Settings',
        permiso: 'EMPRESA.CONFIGURACION.VER',
      },
    ],
  },
  {
    /*
      **«Configurar» se partió en dos, y no se subió el tope.**

      La prueba de navegación avisó al llegar a ocho, y el comentario que la acompaña desde E14 ya
      decía qué corresponde: partir el grupo, porque mezclaba la estructura del comercio con su
      gente y su bitácora. Correr el número de siete a ocho era la salida fácil y la que hace que
      dentro de tres entregas el menú vuelva a ser una lista de veintiséis.

      Configurar es **cómo está armado el comercio y sus documentos**. Administrar es **quién opera,
      qué se trajo de afuera y qué pasó**.
    */
    clave: 'navegacion.grupos.administrar',
    secciones: [
      {
        ruta: '/gente',
        clave: 'navegacion.gente',
        icono: 'UserCog',
        permiso: 'ACCESOS.MEMBRESIA.VER',
      },
      {
        ruta: '/importacion',
        clave: 'navegacion.importacion',
        icono: 'Upload',
        permiso: 'IMPORTACION.LOTE.VER',
      },
      {
        ruta: '/auditoria',
        clave: 'navegacion.auditoria',
        icono: 'History',
        permiso: 'AUDITORIA.REGISTRO.VER',
      },
    ],
  },
];

/** Todas las secciones, sin agrupar. La usan el buscador y la prueba de cobertura. */
export const TODAS: readonly Seccion[] = [
  INICIO,
  ...GRUPOS.flatMap((grupo) => grupo.secciones),
  PLATAFORMA,
];

/**
 * Si la sección se ofrece: con el permiso que pide y, si es fiscal, en un país con fisco
 * electrónico. La usan el menú y el buscador de pantallas, para que no se separen.
 */
export function seccionVisible(
  seccion: Seccion,
  permisos: ReadonlySet<string>,
  tieneFiscoElectronico: boolean,
): boolean {
  if (seccion.soloConFiscoElectronico === true && !tieneFiscoElectronico) return false;
  return seccion.permiso === undefined || permisos.has(seccion.permiso);
}
